//
// The single per-tenant switch for the Cove Club loyalty program.
//
// WHY IT LIVES IN convex/lib RATHER THAN src/lib/tenant-capabilities.ts
// ---------------------------------------------------------------------------
// The gate has to hold SERVER-SIDE first. `orders.create`, `orders.joinRewards`
// and `customerAuth.requestLoginCode`/`verifyLoginCode` are public, unauthenticated
// Convex functions: a UI-only flag stops nothing, because a client that never
// runs the UI can call them directly. Convex modules cannot import from `src/`
// (separate bundles, different tsconfig roots), so a flag that lived only in
// `src/lib/tenant-capabilities.ts` could never be enforced where it matters.
//
// It therefore lives here, and `src/` imports THIS module rather than mirroring
// it, exactly as `src/stores/cart-store.ts` already imports `convex/lib/orderTotals`
// and `src/app/api/support/inbound/route.ts` imports `convex/lib/supportAccounts`.
// One value, one file, imported by both sides: there is no copy to drift.
//
// Keep this module DEPENDENCY-FREE of `_generated/*` and of any Convex server
// import. That is what makes it importable from a browser bundle.
//
// FAIL CLOSED
// ---------------------------------------------------------------------------
// The map is an allowlist of restaurants whose program is announced and live.
// Anything absent, misspelled, undefined or non-string is OFF. A tenant does
// not get a points program by being forgotten.
//
// TURNING A RESTAURANT ON
// ---------------------------------------------------------------------------
// Flip its entry below to `true` (Chef Roma: `"roma-kitchen": false` → `true`)
// and deploy. Nothing else. The rewards tables are already tenant-scoped and
// `awardRewardsForPaidOrder` has been accruing points per tenant all along, so
// switching a restaurant on exposes the balance its guests have already earned.
// It is a switch, not a migration.
//
// Before flipping Chef Roma specifically, ALSO set `RESEND_API_KEY_ROMA` in the
// Convex deployment. Without it `activeSupportAccount` downgrades to Tuscan's
// Resend account, so a Roma guest's SIGN-IN email would leave from a Tuscan
// domain address — a deliverability and trust problem on the one message that
// has to look legitimate.

/**
 * Restaurants whose loyalty program is live, keyed by `restaurantId` (the same
 * id used in `PLATFORM_ADMIN_LOCATIONS` and `LOCATION_EMAIL_CONTEXTS`).
 *
 * Chef Roma is deliberately OFF. The approved design (docs/superpowers/specs/
 * 2026-08-02-cove-club-loyalty-design.md) puts Roma out of scope for pass 1:
 * "The rewards tables are already tenant-scoped, so Roma is a later switch-on,
 * not a migration." Without this map the program went soft-live for Roma on
 * deploy: /club/login would have mailed real sign-in codes, the checkout
 * Rewards card would have rendered a "Roma Club" badge, and welcome bonuses
 * would have been granted for a program nobody had announced.
 */
export const LOYALTY_PROGRAM_ENABLED_RESTAURANT_IDS: Readonly<
  Record<string, boolean>
> = {
  // THIS IS THE LAUNCH SWITCH. It ships `false` DELIBERATELY.
  //
  // Everything the Cove Club needs is built and deployed with this branch, but
  // deploying is not launching. The launch gates — the points-liability report
  // (`loyalty:measurePointsLiability`) and the untenanted-member backfill
  // (`migrations:backfillUntenantedRewardsMembersInternal`) — are supposed to be
  // run "after deploy, before enabling", and neither can run against production
  // until it EXISTS in production. With `true` here the deploy IS the launch:
  // the program would be live for real guests in the same instant the functions
  // that are meant to vet it first arrive.
  //
  // False gives the owner a deployed-but-dark window in which to run those data
  // checks against real production data with nothing customer-facing switched on.
  //
  // FLIPPING THIS TO `true` IS THE LAUNCH. It is a one-word change plus a
  // deploy; there is no migration behind it. See the runbook at
  // .superpowers/sdd/2026-08-02-cove-club-pass-2/task-12-launch-gates-report.md.
  // LAUNCHED 2026-08-03, after all three data gates ran against production:
  // the untenanted-member backfill applied (1 row, untenanted now 0), the
  // liability measured ($16.80 across 14 members, 0 enrolled, 0 able to redeem
  // yet), and the mixed-case customerEmail scan came back clean (0 of 62).
  "tuscan-cove": true,
  // Chef Roma stays dark. Enabling it needs RESEND_API_KEY_ROMA set first, or
  // Roma login mail sends from a Tuscan address.
  "roma-kitchen": false,
};

/**
 * Whether the loyalty program is enabled for a restaurant. Fail-closed:
 * unknown, missing or non-string ids are OFF.
 *
 * Takes `unknown` on purpose. Several call sites hold a `string | undefined`
 * that arrived from an optional Convex arg (`orders.joinRewards`), and a
 * signature that forced them to narrow first is a signature people route
 * around with a non-null assertion.
 */
export function isLoyaltyProgramEnabled(restaurantId: unknown): boolean {
  if (typeof restaurantId !== "string") return false;
  return LOYALTY_PROGRAM_ENABLED_RESTAURANT_IDS[restaurantId] === true;
}
